import { createContext, useContext, useEffect } from 'react';
/**
 * ⭐ THE POLICY LIVES IN A REACT-FREE FILE (2026-09-20). ⛔ Not tidiness — a deploy
 * aborted because `rail-slot-test.cjs` cannot load a module that imports react from
 * `tools/`. See the header of `railSlotPolicy.ts`.
 * ⚠️ RE-EXPORTED, so every existing importer of `railSlot` keeps working and nothing
 * outside this pair needs to know the file was split.
 */
export {
    RAIL_PANEL_ORDER, MAX_OPEN_RAIL_PANELS, nextRailSlots, railSlotReducer,
} from '@/lib/railSlotPolicy';

/**
 * WHICH RAIL CARD IS OPEN — REPORTED UPWARD, NOT OWNED UPWARD.
 *
 * ---- ⭐ WHAT PETE ASKED FOR -------------------------------------------------
 *
 * > Pete, 2026-09-09: *"when you expand a panel like Labor, it should go all the
 * > way and bump up against the panel above, not white space … It would be even
 * > better if the opened panel went to the top of the list of the 3 panels (but
 * > still below the driver section)."*
 *
 * ⛔ BOTH HALVES NEED ONE FACT THE RAIL DID NOT HAVE: **which card is open.**
 * Sizing needs it (the open one takes the slack, the closed ones keep their
 * header height) and ordering needs it (the open one moves to the top).
 *
 * ---- ⛔⛔ WHY THE STATE IS NOT SIMPLY LIFTED INTO `CheckInPanel` ------------
 *
 * ⚠️ The obvious move — hoist `open` out of each panel and pass it down — cannot
 * reach the paperwork card. `CheckInPanel` receives it as an opaque `footer`
 * ReactNode from `Calendar`, precisely because migration 162's rule decides
 * WHICH paperwork component mounts (`PaperworkSliver` on a SharePoint site,
 * `ScanFolderPanel` on a local-folder one) and the rail must not know or care.
 *
 * ⛔ THE ALTERNATIVES WERE WORSE:
 *   · `cloneElement(footer, …)` — injecting props into a node whose type this
 *     file is deliberately ignorant of. It fails silently the day `Calendar`
 *     wraps that node in anything.
 *   · changing `footer` to a render function — a breaking prop change in a
 *     165 KB file, to move a boolean.
 *
 * ⭐ SO EACH PANEL KEEPS ITS OWN `open` AND REPORTS IT. One line per panel, no
 * prop contracts touched, and a panel that never calls the hook simply behaves
 * as it does today rather than breaking.
 *
 * ---- ⚠️ THE REPORT IS A NO-OP WHEN NOTHING CHANGED, AND THAT IS LOAD-BEARING -
 *
 * ⛔ The receiver MUST compare before it sets state. Report → setState →
 * re-render → effect → report is an infinite loop, and it renders as a frozen
 * tab rather than an error. `railSlotReducer` below exists so every consumer
 * gets that comparison for free instead of remembering it.
 *
 * ---- ⚠️ AND IT REPORTS `false` ON UNMOUNT ----------------------------------
 *
 * ⛔ A card switched off in the Right-Side Toolbar config unmounts while OPEN.
 * Without the cleanup the rail would keep a slot reserved, and its space, for a
 * panel that is no longer on screen — a gap nobody can close because the control
 * that would close it is gone.
 */

/** ⚠️ `null` means "no rail above me" — a panel rendered outside the rail. */
export type RailSlotReport = (code: string, open: boolean) => void;

/**
 * ---- ⭐⭐ THE RAIL IS AN ACCORDION (2026-09-10) ---------------------------
 *
 * > Pete: *"When you do the chevron on Labor, Attached Paperwork, WMS Orders or
 * > Communications, the panel should go to it highest vertical point allowed in
 * > the column and the other panels should close there chevrons and fall to the
 * > bottom."*
 *
 * ⭐ HALF OF THAT ALREADY WORKED. `slotProps` gives the open panel `order: 0`
 * and the closed ones `order: 1`, so it rises and they sink — that is the
 * "highest vertical point" and the "fall to the bottom".
 *
 * ⛔ WHAT DID NOT: TWO PANELS COULD BE OPEN AT ONCE, and then `flex-1` on both
 * split the column between them — so opening a second one HALVED the first, and
 * neither reached the top. ⚠️ That is what made the rail feel like it would not
 * expand however many chevrons you pressed.
 *
 * ---- ⛔⛔ THE REPORT HAD TO BECOME A CONVERSATION, AND ONLY JUST ----------
 *
 * ⚠️ THIS FILE'S WHOLE PREMISE IS "REPORTED UPWARD, NOT OWNED UPWARD" — each
 * panel keeps its own `open` because `CheckInPanel` receives the paperwork card
 * as an opaque `footer` and must not learn its type. ⛔ AN ACCORDION NEEDS THE
 * RAIL TO CLOSE A PANEL, which is exactly what that premise forbids.
 *
 * ⭐ SO THE RAIL DOES NOT CLOSE ANYTHING. It publishes WHICH CODE IS OPEN, and
 * ⛔⚤ ~~`useRailSlot` returns "somebody else is open"~~ — IT RETURNED `void` FOR TEN
 * DAYS. The sentence described the accordion that was reverted the same day it was
 * written, and it sat here as a true-sounding account of code that did not exist.
 * ⭐ TRUE AGAIN SINCE 2026-09-20 (decision 193), in a narrower form: it returns
 * "THE RAIL IS ASKING **YOU** TO CLOSE", a name rather than a membership test.
 *   ** A COMMENT DESCRIBING A REVERTED DESIGN IS WORSE THAN NO COMMENT: IT READS AS
 *      DOCUMENTATION AND IT IS FICTION. **
 * (original sentence) `useRailSlot` returns "somebody else is open" to the panel, which closes
 * ITSELF. ⚠️ One boolean down, one report up; no callback registry, no
 * `cloneElement`, and a panel that ignores the return value simply behaves as it
 * does today rather than breaking.
 *
 * ⛔⛔ AND THE LOOP THIS COULD HAVE BEEN. report -> setState -> render ->
 * effect -> report is the infinite one this file already warns about; adding a
 * DOWNWARD signal opens a second: yield -> setOpen(false) -> report(false) ->
 * openCode clears -> yield goes false -> ... ⭐ It terminates because
 * `report(code, false)` for a code that is NOT `openCode` is a no-op (see
 * `railSlotReducer`), so the closing panel never disturbs the one that opened.
 */
export interface RailSlot {
    report: RailSlotReport;
    /**
     * ⛔ ~~`openCodes`, THE ACCORDION'S SLATE~~ — REVERTED THE SAME DAY (2026-09-10).
     * ⭐⭐ AND THE REASON IT FAILED IS THE REASON `evicted` IS SHAPED THE WAY IT IS.
     * Publishing MEMBERSHIP means a panel asks *"am I in the open set?"* — and on the
     * frame it sets `open = true` its own report has not run yet, so the answer is NO
     * and it closes itself on the way open. **Absence is indistinguishable from
     * not-yet-reported.**
     *   ** A PANEL CANNOT LEARN ITS FATE FROM A SET IT HAS NOT YET JOINED. **
     */

    /**
     * ---- ⭐⭐ THE ONE PANEL THE RAIL IS ASKING TO CLOSE (decision 193, 2026-09-20) --
     *
     * ⭐ Pete: *"is it possible to only have two chevrons in the right panel open at
     * one time? One is ideal, two is the max … if they open a third, one of the other
     * minimizes, probably the one on the bottom - like it replaces it."*
     *
     * ⛔ AN EXPLICIT NAME, NOT AN ABSENCE. Exactly one code at a time, set only when
     * the cap actually evicts somebody, and `null` the rest of the time. A panel that
     * has never been mentioned is simply not evicted — which is what makes the
     * first-frame problem above impossible to reproduce here.
     *
     * ⚠️ IT IS SELF-CLEARING. Every report that changes the open set writes this
     * field, with `null` when nothing was evicted, so a stale name cannot survive to
     * evict the same panel a second time when it re-opens. See `nextRailSlots`.
     */
    evicted: string | null;

    /**
     * ---- ⛔⚤ ~~`collapseKey` — "EVERYBODY CLOSE"~~ — **DELETED 2026-09-20** --------
     *
     * ⛔⛔ DECISION 190 C IS REVERSED BY DECISION 194 (§1AV.179). Pete, the same day:
     *
     * > ⭐ *"changed my mind on the panels. When scope changes, lets keep the panels we
     * > had open with the chevron still open so we can see the info change. But when we
     * > hit the circle R, it should collaspe the driver, client, carrier, consignee
     * > cards like it does now, but not collaspe the right side panels."*
     *
     * ⭐⭐ AND HIS REASON IS BETTER THAN THE ORIGINAL'S. 190 C argued *"a fresh start on
     * a record"*; this argues *"so we can see the info change"* — the rail IS the
     * instrument, and closing the instrument at the moment the reading changes is the
     * one time you need it open.
     *   ** A PANEL YOU OPENED IS A QUESTION YOU ASKED. CHANGING THE SUBJECT IS NOT AN
     *      ANSWER TO IT. **
     *
     * ⛔ THE WHOLE MECHANISM IS GONE — field, state, ref, effect and the watcher in
     * `useRailSlotClose` — not merely left unwritten. A counter nobody increments, with
     * a comment explaining what it is for, is exactly the fiction this file was cleaned
     * of on 2026-09-20.
     *   ** WHEN A REVERT LANDS, THE COMMENTS THAT ARGUED FOR IT ARE PART OF THE REVERT. **
     *
     * ⚠️ `evicted` IS UNAFFECTED AND STILL NEEDED. It names ONE panel because the
     * two-panel cap pushed it out (decision 193) — a different fact with a different
     * lifetime, and the cap is not reversed.
     */
}


/* ⚠️ FIVE CODES, SIX CALL SITES. `PaperworkSliver` (Attached) and `ScanFolderPanel`
   (Scanned) BOTH report `PAPERWORK`, gated by `active` so only the visible one speaks
   — see the long note on `useRailSlot`. ⛔ They are ONE slot on purpose; giving the
   scanned tray its own code would let both be open and put three panels on screen. */


export const RailSlotContext = createContext<RailSlot | null>(null);

/**
 * Call once, in a rail panel, with its vocabulary code and its own open state.
 *
 * ⭐ THE CODE MUST MATCH `dock.RailPanel.PanelCode` — `CHECKIN`, `PAPERWORK`,
 * `LABOUR`, `COMMS`. The same string already decides whether the card renders at
 * all (`panelOn`), so a typo here produces a card that shows and never moves,
 * which is a much quieter failure than one that does not show.
 *
 * ---- ⛔⛔ THAT COMPLAINT CAME TRUE ON 2026-09-10 ---------------------------
 *
 * > The note here used to read: *"BOTH paperwork components report `PAPERWORK`.
 * > Exactly one is ever mounted (migration 162), so they cannot collide — and if
 * > that ever stopped being true the rail would show one slot for two cards,
 * > which is the correct complaint."*
 *
 * ⛔ §1R.16 WAS REVERSED THE SAME DAY. Both components are now MOUNTED — each
 * runs its hooks and then returns `null` when `sourceView` says the other one is
 * the visible pile. ⚠️ A component that renders NOTHING was still reporting a
 * slot, and reporting `open: false` for it, so the HIDDEN tray could switch the
 * rail's PAPERWORK slot from `flex-1` to `shrink-0` under the VISIBLE one.
 *
 * ⛔⛔ AND THE SYMPTOM WAS NOT "THE PANEL IS THE WRONG HEIGHT" — IT WAS
 * "THERE IS NO SCROLLBAR":
 *
 * > Pete, three times: *"Still no scrollbar. Can't see the two docs there"* …
 * > *"also still no scrollbar"*.
 *
 * A `shrink-0` slot sizes to its CONTENT, so the body never gets a height
 * smaller than what it holds, `overflow-y-auto` has nothing to overflow, and the
 * rail's `overflow-hidden` simply cuts the bottom off. ⚠️ Measured, not
 * reasoned: with the slot bounded the body scrolls (client 232 of 908); with it
 * content-sized the body reports client 908 and `scrollable: false` while its
 * parent is 191px tall. Same classes on the body in both cases — which is why
 * three rounds of "the scroll classes are present" were true and useless.
 *
 * ⭐ WHICH ORDER WINS WAS A RACE, and that is why it came and went. Effects
 * re-run only when their deps change, so the last of the two to change decided
 * the slot.
 *
 * ---- ⭐ `active` — A COMPONENT THAT DRAWS NOTHING REPORTS NOTHING ----------
 *
 * ⛔ NOT "REPORTS FALSE". False is a claim about the slot, and the hidden tray
 * has no standing to make one. ⚠️ The cleanup is skipped with it, so a tray
 * going hidden does not clear the slot the newly-visible one just claimed:
 * React runs every cleanup before any effect, so on the render where the source
 * flips, the outgoing tray's cleanup fires first and the incoming tray's effect
 * fires after it — the slot ends up true, whichever way the switch went.
 *
 * ⛔ IT DEFAULTS TO `true`, so every OTHER panel keeps today's behaviour without
 * being touched.
 */
export function useRailSlot(code: string, open: boolean, active = true): boolean {
    const slot = useContext(RailSlotContext);
    const report = slot?.report;
    useEffect(() => {
        if (!report || !active) return;
        report(code, open);
        return () => report(code, false);
    }, [report, code, open, active]);
    /**
     * ---- ⭐⭐ THE RETURN: "THE RAIL IS ASKING YOU TO CLOSE" (decision 193) --------
     *
     * ⛔⛔ READ DURING RENDER, NOT IN AN EFFECT, and that is the whole difference from
     * the version reverted on 2026-09-10. An effect would deliver this a frame late
     * and the panel would fight the one that just opened.
     *
     * ⭐ AND IT IS A NAME, NOT AN ABSENCE. `evicted === code` is true only when the
     * cap actually pushed this panel out; a panel that has simply not reported yet is
     * not evicted, so it cannot close itself on the way open.
     *   ** A PANEL CANNOT LEARN ITS FATE FROM A SET IT HAS NOT YET JOINED. **
     *
     * ⚠️ `active` GATES IT for the same reason it gates the report: a component that
     * draws nothing has no standing to be evicted, and acting on it would close the
     * VISIBLE twin that shares its code.
     */
    return active && slot?.evicted === code;
}


/**
 * ⭐ THE COMPARISON THAT STOPS THE LOOP. Returns the SAME value when nothing
 * changed, so React bails out of the re-render entirely.
 *
 * ⛔⚤ ~~"A `Set` — IT IS ONE CODE NOW"~~ — THAT WAS NEVER TRUE OF THIS FUNCTION.
 * It has taken and returned `Set<string>` throughout, and `CheckInPanel` has held
 * `openPanels` as a Set throughout. The sentence described the accordion reverted on
 * 2026-09-10 and then sat here for ten days flatly contradicting the signature three
 * lines below it. ⚠️ Found 2026-09-20 while building decision 193 ON this file —
 * which is the expensive way to find it.
 *   ** WHEN A REVERT LANDS, THE COMMENTS THAT ARGUED FOR IT ARE PART OF THE REVERT. **
 *
 * ⛔ SUPERSEDED BY `nextRailSlots`, which does this AND applies the two-panel cap in
 * one pure step. This function is kept only because the bail-out behaviour below is
 * still exactly right and `nextRailSlots` delegates the membership half to it.
 *
 * ⚠️ `report(code, false)` FOR A CODE THAT IS NOT THE OPEN ONE IS A NO-OP, and
 * that is load-bearing: it is what stops a panel closing itself in response to
 * another panel opening from also clearing the newly-opened one.
 */
/**
 * ---- ⭐⭐ ONE HOOK, SO SIX PANELS DO NOT CARRY SIX COPIES (decision 193) ---------
 *
 * ⛔ EVERY RAIL PANEL NEEDS THE SAME THREE LINES — report, read the eviction, close
 * itself — and six hand-written copies is how five of them stay right and one drifts.
 * That is the shape this file already avoided once for `report`.
 *   ** A RULE EVERY CALLER MUST APPLY IS A RULE ONE CALLER WILL FORGET. **
 *
 * ⚠️ `setOpen` MUST BE STABLE, and the `useState` setter is — passing an inline
 * arrow would re-run this effect every render and close the panel repeatedly.
 *
 * ⛔ `&& open` IS NOT REDUNDANT. A panel can be named as evicted and already be
 * closed (it closed itself last frame); calling `setOpen(false)` again would be a
 * harmless no-op today and a real re-render the moment anything else keys off it.
 */
export function useRailSlotClose(
    code: string,
    open: boolean,
    setOpen: (next: false) => void,
    active = true,
): void {
    /* ⛔⚤ ~~`const slot = useContext(RailSlotContext)`~~ — REMOVED 2026-09-20 with
       `collapseKey`, its only reader. ⚠️ `noUnusedLocals` is what says so out loud;
       leaving it would have compiled a subscription to a context this hook no longer
       reads, re-rendering all six panels on every eviction for nothing.
         ** DELETING A FEATURE MEANS DELETING WHAT FED IT, NOT JUST WHAT USED IT. ** */
    const evicted = useRailSlot(code, open, active);
    useEffect(() => {
        if (evicted && open) setOpen(false);
    }, [evicted, open, setOpen]);

    /* ⛔⚤ ~~AND THE "EVERYBODY CLOSE" EDGE~~ — **DELETED 2026-09-20, decision 194.**
       A scope change now collapses NOTHING; see the struck block on `RailSlot` above.
       ⚠️ `slot` is still read for `evicted`, which the cap (decision 193) needs. */
}


